// src/hooks/useTeams.ts

import { useState, useCallback, useEffect, useRef } from "react";
import { TeamModel } from "../models/TeamModel";
import { teamService } from "../services/teamService";

const TEAMS_PAGE_SIZE = 200; // Teams are few — load all at once

export type TeamsSortOption = "lastEntryAt" | "recentEntryCount";

/**
 * @param autoLoad          Load the first page on mount.
 * @param showRookieEntries Viewer preference. Forwarded to the API so the badge
 *        counts (and the "most active" ordering) exclude rookie-authored entries
 *        when the viewer has them hidden — otherwise the pane advertised entries
 *        the team page never rendered. Mirrors useCustomTopics.
 */
export const useTeams = (autoLoad: boolean = true, showRookieEntries: boolean = true) => {
  const [teams, setTeams]             = useState<TeamModel[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages]   = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [sortBy, setSortBy]           = useState<TeamsSortOption>("recentEntryCount");
  // Left-pane country filter (parent club country) — null means all countries
  const [countryFilter, setCountryFilter] = useState<string | null>(null);
  // Kept in a ref so loadTeams stays a stable useCallback while still reading the
  // current preference value (same stale-closure guard used by useCustomTopics).
  const showRookieRef = useRef(showRookieEntries);
  showRookieRef.current = showRookieEntries;

  /**
   * Core loader. Pass `append=true` to add the next page to the list.
   * Pass `silent=true` to skip the loading flag so the existing list
   * stays visible during refresh (matches pane Yenile behaviour).
   */
  const loadTeams = useCallback(async (
    sort: TeamsSortOption = "recentEntryCount",
    page = 0,
    append = false,
    silent = false,
    country: string | null = null
  ) => {
    if (append) {
      setIsLoadingMore(true);
    } else if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const sortDir = "desc"; // both sorts are descending (most recent / most active first)
      const result = await teamService.getAllTeams(page, TEAMS_PAGE_SIZE, sort, sortDir, country, showRookieRef.current);
      if (append) {
        setTeams(prev => [...prev, ...result.content]);
      } else {
        setTeams(result.content);
      }
      setCurrentPage(page);
      setTotalPages(result.totalPages);
    } catch {
      setError("Takımlar yüklenemedi.");
    } finally {
      if (!silent) setLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  // Load on mount — skipped when autoLoad is false so the parent can lazy-load
  // this pane only when the user first switches to it.
  useEffect(() => {
    if (autoLoad) loadTeams("recentEntryCount", 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad]);

  // Reload when the viewer flips the "show rookie entries" preference, so the
  // badge counts refresh without requiring a manual Yenile.
  const isFirstPrefRun = useRef(true);
  useEffect(() => {
    if (isFirstPrefRun.current) {
      isFirstPrefRun.current = false;
      return;
    }
    loadTeams(sortBy, 0, false, true, countryFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRookieEntries]);

  const loadMoreTeams = useCallback(async () => {
    const nextPage = currentPage + 1;
    if (isLoadingMore || nextPage >= totalPages) return;
    await loadTeams(sortBy, nextPage, true, false, countryFilter);
  }, [currentPage, totalPages, isLoadingMore, loadTeams, sortBy, countryFilter]);

  /**
   * Silent refresh — fetches fresh data without triggering the loading state,
   * so the list stays visible during the request (matches pane behaviour).
   */
  const refreshTeams = useCallback(async () => {
    await loadTeams(sortBy, 0, false, true, countryFilter);
  }, [loadTeams, sortBy, countryFilter]);

  const handleSortChange = useCallback((newSort: TeamsSortOption) => {
    setSortBy(newSort);
    loadTeams(newSort, 0, false, false, countryFilter);
  }, [loadTeams, countryFilter]);

  /**
   * Left-pane country filter change — resets to page 0 and reloads.
   * Pass null to clear the filter (all countries).
   */
  const handleCountryFilterChange = useCallback((country: string | null) => {
    setCountryFilter(country);
    loadTeams(sortBy, 0, false, false, country);
  }, [loadTeams, sortBy]);

  return {
    teams,
    loading,
    error,
    currentPage,
    totalPages,
    isLoadingMore,
    sortBy,
    countryFilter,
    loadTeams,
    loadMoreTeams,
    refreshTeams,
    handleSortChange,
    handleCountryFilterChange,
  };
};